import { Chip, Stack } from '@mui/material'
import { YALO_RADIUS, YALO_SEMANTIC } from '@commerce/tokens'

interface Props {
  categories: readonly string[]
  selected: string
  onSelect: (category: string) => void
}

/**
 * The category rail (reference.html:784–791).
 *
 * `component="button"` matters: a MUI `Chip` with an `onClick` is a `<div>`
 * carrying `role="button"`, and this rail is the screen's only filter. The
 * selected chip is announced through `aria-pressed` rather than through its
 * colour alone.
 *
 * The categories are the ones the catalog query came back with — this component
 * neither knows nor invents them.
 */
export const CategoryChips = ({ categories, selected, onSelect }: Props) => (
  <Stack
    direction="row"
    spacing={1}
    role="group"
    aria-label="Categorías"
    sx={{
      overflowX: 'auto',
      pb: 0.5,
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
      '& > *': { flexShrink: 0 },
    }}
  >
    {categories.map((category) => {
      const active = category === selected
      return (
        <Chip
          key={category}
          component="button"
          type="button"
          label={category}
          aria-pressed={active}
          onClick={() => onSelect(category)}
          sx={{
            height: 36,
            px: 0.5,
            borderRadius: `${YALO_RADIUS.pill}px`,
            fontSize: 13.5,
            fontWeight: 600,
            cursor: 'pointer',
            border: `1px solid ${active ? 'transparent' : YALO_SEMANTIC.borderDefault}`,
            bgcolor: active ? 'primary.main' : YALO_SEMANTIC.bgSurface,
            color: active ? 'primary.contrastText' : YALO_SEMANTIC.fg2,
            '&:hover': { bgcolor: active ? 'primary.main' : YALO_SEMANTIC.bgTint },
          }}
        />
      )
    })}
  </Stack>
)
